import { ChangeDetectionStrategy, Component, computed, inject, input, InjectionToken, Injector } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AccordionComponent } from '../../accordion/accordion';
import { AccordionItemComponent } from '../../accordion/accordion-item';
import { EditStateCoordinator } from '../edit-state-coordinator.service';
import type { EntityEditSection } from '../entity-edit-section.types';

@Component({
  selector: 'zag-entity-edit-sections-host',
  templateUrl: './sections-host.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, TranslatePipe, AccordionComponent, AccordionItemComponent],
})
export class EntityEditSectionsHostComponent {
  private readonly injector = inject(Injector);
  private readonly coordinator = inject(EditStateCoordinator);
  private readonly route = inject(ActivatedRoute, { optional: true });

  /** Token to inject sections from */
  readonly sectionsToken = input.required<InjectionToken<EntityEditSection[]>>();

  /**
   * Section id to expand initially, taken from the `?section=<id>` query
   * param (story 35-3, U5: deep links like the balance overview open the
   * target section directly). `null` = all sections start collapsed.
   */
  readonly initialExpandedId: string | null = this.route?.snapshot.queryParamMap.get('section') ?? null;

  /** Sections injected via the token */
  private readonly providedSections = computed(() => {
    try {
      // Get all provided section arrays and flatten them
      const token = this.sectionsToken();
      const arrays = this.injector.get(token, [], { optional: true });
      // If provided with multi: true, it will be an array of arrays
      return Array.isArray(arrays) && arrays.length > 0 && Array.isArray(arrays[0])
        ? arrays.flat()
        : (arrays as unknown as EntityEditSection[]);
    } catch {
      return [];
    }
  });

  /** Filtered and sorted sections ready for rendering */
  readonly sections = computed(() => {
    const isNew = this.coordinator.isNew();

    return this.providedSections()
      .filter(section => {
        // Hide if it requires an entity ID and we are creating a new one
        if (isNew && section.requiresEntityId) {
          return false;
        }

        // Custom visibility check
        if (section.visibilityCheck && !section.visibilityCheck(this.injector)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.priority - b.priority);
  });

  getBadge(sectionId: string): number | null {
    const badges = this.coordinator.sectionBadges();
    return badges[sectionId] ?? null;
  }
}
